import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BadgeCheck, Upload } from "lucide-react";

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-2.5 rounded-sm text-sm text-white";

export default function VerificationCard() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [org, setOrg] = useState("");
  const [message, setMessage] = useState("");
  const [proofPath, setProofPath] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/verification-requests/me").then(({ data }) => setStatus(data.status));
  }, []);

  const uploadProof = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post("/upload/image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setProofPath(data.path);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/verification-requests", { organization: org, message, proof_path: proofPath });
      setStatus("pending");
      setOpen(false);
      toast.success("Verification request sent — you'll get the badge once approved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  if (status === null || status === "approved") return null;

  return (
    <div className="bg-[#121212] border border-[#404040] rounded-sm p-5" data-testid="verification-card">
      <div className="flex items-center gap-2 text-white font-semibold">
        <BadgeCheck className="w-5 h-5" />
        {status === "pending" ? "Verification pending" : "Get verified"}
      </div>
      {status === "pending" ? (
        <p className="text-sm text-[#a3a3a3] mt-1" data-testid="verification-pending">
          Your request is being reviewed. Verified scouts can message youth players directly.
        </p>
      ) : (
        <>
          <p className="text-sm text-[#a3a3a3] mt-1">
            {status === "rejected"
              ? "Your last request was declined. You can apply again with more proof."
              : "Submit your club or organization details to earn the verified badge and message youth players directly."}
          </p>
          {!open ? (
            <button
              data-testid="verification-open-btn"
              onClick={() => setOpen(true)}
              className="mt-3 px-5 py-2 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 text-sm"
            >
              Request verification
            </button>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3">
              <input
                data-testid="verification-org"
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                placeholder="Club / organization (e.g. Arsenal Academy)"
                className={inputCls}
                required
                minLength={2}
              />
              <textarea
                data-testid="verification-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us about your role and how to verify you (staff page link, etc.)"
                rows={3}
                className={`${inputCls} resize-none`}
              />
              <label className="flex items-center gap-2 cursor-pointer border border-dashed border-[#404040] hover:border-white p-3 rounded-sm text-[#a3a3a3] hover:text-white text-sm transition-colors">
                <Upload className="w-4 h-4" />
                {proofPath ? "Proof uploaded ✓" : "Upload ID / club proof (image)"}
                <input
                  data-testid="verification-proof-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadProof(e.target.files?.[0])}
                />
              </label>
              <button
                data-testid="verification-submit"
                disabled={busy}
                className="px-5 py-2 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 text-sm disabled:opacity-60"
              >
                {busy ? "Submitting…" : "Submit request"}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
}
